import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roleApi, type RolesListParams } from '../api/role.api';
import type { RoleInput } from '@cms/models';

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    queryFn: () => roleApi.getAll(),
  });
}

export function useRolesPaginated(params: RolesListParams = {}) {
  const { page = 1, pageSize = 25, search, status, sortBy, sortOrder } = params;
  return useQuery({
    queryKey: [
      'roles',
      'paginated',
      page,
      pageSize,
      search ?? '',
      status ?? '',
      sortBy ?? '',
      sortOrder ?? '',
    ],
    queryFn: () =>
      roleApi.getPaginated({
        page,
        pageSize,
        search: search || undefined,
        status,
        sortBy,
        sortOrder,
      }),
  });
}

export function useRole(id: string) {
  return useQuery({
    queryKey: ['role', id],
    queryFn: () => roleApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RoleInput) => roleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RoleInput> }) =>
      roleApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['role', variables.id] });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => roleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
}
